import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { rowsToCsv, type CsvRow } from './csvExport';
import { createZipBlob, downloadBlob, type ZipFile } from './zipExport';

const PAGE_SIZE = 1000;

type TableCandidate = {
  table: string;
  columns?: readonly string[];
  hasDeletedAt?: boolean;
};

type ExportDefinition = {
  exportName: string;
  candidates: readonly TableCandidate[];
};

export type BackupFailure = {
  table: string;
  sourceTable: string;
  message: string;
};

export type BackupManifest = {
  application: 'CourtCaptain';
  version: 1;
  exportedAt: string;
  exportedBy: string;
  tableCount: number;
  tables: string[];
  sourceTables: Record<string, string>;
  skippedTables: string[];
  failures: BackupFailure[];
};

export type BackupResult = {
  fileName: string;
  manifest: BackupManifest;
};

type BackupOptions = {
  exportedBy: string;
  isAdmin: boolean;
  now?: Date;
};

const auditColumns = ['created_at', 'updated_at', 'created_by', 'updated_by', 'deleted_at', 'deleted_by', 'delete_reason'] as const;

const exportDefinitions: readonly ExportDefinition[] = [
  {
    exportName: 'workspaces',
    candidates: [{
      table: 'workspaces',
      columns: ['id', 'name', 'description', 'active_status', 'created_at', 'updated_at'],
    }],
  },
  {
    exportName: 'workspace_branding',
    candidates: [{
      table: 'workspace_branding',
      columns: [
        'id', 'workspace_id', 'display_name', 'tagline', 'primary_color', 'secondary_color', 'accent_color',
        'logo_url', 'portal_icon_url', 'app_name', 'short_name', 'created_at', 'updated_at',
      ],
    }],
  },
  {
    exportName: 'players',
    candidates: [{
      table: 'players',
      hasDeletedAt: true,
      columns: [
        'id', 'first_name', 'last_name', 'email', 'mobile_phone', 'birthday', 'address_line1', 'address_line2',
        'city', 'state', 'zip_code', 'utr_rating', 'utr_singles_rating', 'utr_doubles_rating', 'utr_profile_url',
        'tennis_record_profile_url', 'tennis_record_external_id',
        'tennis_record_display_name', 'tennis_record_last_sync_attempt_at', 'tennis_record_last_successful_sync_at',
        'tennis_record_sync_status', 'tennis_record_last_sync_error', 'tennis_record_last_page_hash',
        'usta_profile_url', 'ntrp_rating', 'estimated_dynamic_rating',
        'estimated_dynamic_rating_updated_at', 'estimated_dynamic_rating_source_url', 'sex', 'is_active', 'notes',
        ...auditColumns,
      ],
    }],
  },
  {
    exportName: 'teams',
    candidates: [{
      table: 'teams',
      hasDeletedAt: true,
      columns: [
        'id', 'workspace_id', 'team_name', 'short_name', 'season_id', 'league_configuration_id', 'organization_id', 'team_gender_type', 'team_color', 'team_icon_url',
        'league_name', 'team_flight', 'usta_team_number', 'team_page_url', 'home_match_opponent_message_template', 'home_facility_id',
        'player_portal_access_enabled',
        'player_portal_tentative_lineups_enabled', 'player_portal_tentative_lineup_lookahead_matches',
        'default_target_match_count_per_player', 'default_warmup_minutes', 'is_active', 'notes', ...auditColumns,
      ],
    }],
  },
  {
    exportName: 'seasons',
    candidates: [{
      table: 'seasons',
      hasDeletedAt: true,
      columns: ['id', 'season_name', 'year', 'start_date', 'end_date', 'is_active', 'notes', ...auditColumns],
    }],
  },
  {
    exportName: 'facilities',
    candidates: [{
      table: 'facilities',
      hasDeletedAt: true,
      columns: [
        'id', 'facility_name', 'address_line1', 'address_line2', 'city', 'state', 'zip_code', 'google_maps_url',
        'warmup_booking_url', 'is_active', 'notes', ...auditColumns,
      ],
    }],
  },
  {
    exportName: 'team_rosters',
    candidates: [{
      table: 'team_rosters',
      hasDeletedAt: true,
      columns: [
        'id', 'team_id', 'player_id', 'target_match_count', 'player_tier', 'roster_status', 'roster_role',
        'include_substitute_in_fairness', 'registration_status', 'registration_confirmed_at',
        'registration_confirmed_by', 'is_active', 'notes', ...auditColumns,
      ],
    }],
  },
  {
    exportName: 'app_users',
    candidates: [{
      table: 'app_users',
      hasDeletedAt: true,
      columns: ['id', 'auth_user_id', 'display_name', 'email', 'role', 'linked_player_id', 'is_active', ...auditColumns],
    }],
  },
  {
    exportName: 'captain_assignments',
    candidates: [
      { table: 'captain_assignments', hasDeletedAt: true },
      {
        table: 'team_captain_assignments',
        hasDeletedAt: true,
        columns: [
          'id', 'team_id', 'app_user_id', 'assignment_role', 'is_primary_captain', 'start_date', 'end_date',
          'is_active', 'notes', ...auditColumns,
        ],
      },
    ],
  },
  {
    exportName: 'match_events',
    candidates: [{
      table: 'match_events',
      hasDeletedAt: true,
      columns: [
        'id', 'team_id', 'season_id', 'league_configuration_id', 'event_id', 'match_classification', 'match_date',
        'match_time', 'warmup_time', 'opponent_name', 'opponent_captain_names', 'opponent_captain_email',
        'opponent_captain_phone', 'opponent_co_captain_name', 'opponent_co_captain_email', 'opponent_co_captain_phone', 'opponent_average_edr',
        'opponent_tennis_record_roster_url', 'opponent_tennis_record_team_name', 'opponent_edr_overall',
        'opponent_edr_men', 'opponent_edr_women', 'opponent_edr_median', 'opponent_edr_high', 'opponent_edr_low',
        'opponent_roster_count', 'opponent_rated_player_count', 'opponent_edr_last_refresh_attempt_at',
        'opponent_edr_last_successful_refresh_at', 'opponent_edr_refresh_status', 'opponent_edr_refresh_error',
        'opponent_tennis_record_last_page_hash', 'opponent_edr_source', 'manual_opponent_edr', 'facility_id',
        'match_site_type', 'court_numbers', 'lifecycle_state', 'match_status', 'lineup_status', 'lineup_published_at',
        'lineup_published_by', 'lineup_locked_at', 'lineup_locked_by', 'completed_at', 'completed_by', 'notes_summary',
        ...auditColumns,
      ],
    }],
  },
  {
    exportName: 'player_availability',
    candidates: [
      { table: 'player_availability', hasDeletedAt: true },
      {
        table: 'availability_records',
        hasDeletedAt: true,
        columns: [
          'id', 'match_event_id', 'player_id', 'availability_status', 'last_updated_at', ...auditColumns,
        ],
      },
    ],
  },
  {
    exportName: 'player_availability_history',
    candidates: [
      { table: 'player_availability_history' },
      {
        table: 'availability_history',
        columns: [
          'id', 'availability_record_id', 'match_event_id', 'player_id', 'previous_status', 'new_status',
          'changed_at', 'changed_by',
        ],
      },
    ],
  },
  {
    exportName: 'lineups',
    candidates: [{ table: 'lineups', hasDeletedAt: true }],
  },
  {
    exportName: 'lineup_assignments',
    candidates: [
      { table: 'lineup_assignments', hasDeletedAt: true },
      {
        table: 'lineup_entries',
        hasDeletedAt: true,
        columns: [
          'id', 'match_event_id', 'league_configuration_position_id', 'position_code', 'slot_number', 'player_id',
          ...auditColumns,
        ],
      },
    ],
  },
  {
    exportName: 'standby_assignments',
    candidates: [
      { table: 'standby_assignments', hasDeletedAt: true },
      {
        table: 'standby_players',
        hasDeletedAt: true,
        columns: ['id', 'match_event_id', 'player_id', 'notes', ...auditColumns],
      },
    ],
  },
  {
    exportName: 'player_participation',
    candidates: [{ table: 'player_participation', hasDeletedAt: true }],
  },
  {
    exportName: 'partner_preferences',
    candidates: [
      {
        table: 'partner_preferences',
        hasDeletedAt: true,
        columns: [
          'id', 'workspace_id', 'team_id', 'player1_id', 'player2_id', 'compatibility', 'pairing_rule', 'is_active', 'notes',
          'player_id', 'preferred_partner_id', 'preference_weight',
          ...auditColumns,
        ],
      },
    ],
  },
  {
    exportName: 'participation_events',
    candidates: [
      { table: 'participation_events', hasDeletedAt: true },
      {
        table: 'player_participation_events',
        hasDeletedAt: true,
        columns: [
          'id', 'match_event_id', 'player_id', 'league_configuration_position_id', 'participation_event_type',
          'related_player_participation_event_id', 'replaced_player_id', 'replacement_player_id', 'occurred_at',
          'allocation_count_indicator', 'manual_allocation_override_indicator', 'participation_event_reason',
          'participation_event_notes', ...auditColumns,
        ],
      },
    ],
  },
  {
    exportName: 'team_events',
    candidates: [
      { table: 'team_events', hasDeletedAt: true },
      {
        table: 'events',
        hasDeletedAt: true,
        columns: [
          'id', 'team_id', 'season_id', 'event_type', 'title', 'description', 'event_date', 'start_time', 'end_time',
          'facility_id', 'location_text', 'status', 'visibility', 'recurrence_series_id', 'notes', ...auditColumns,
        ],
      },
    ],
  },
  {
    exportName: 'event_rsvps',
    candidates: [{
      table: 'event_rsvps',
      hasDeletedAt: true,
      columns: [
        'id', 'event_id', 'player_id', 'rsvp_status', 'response_note', 'responded_at', ...auditColumns,
      ],
    }],
  },
] as const;

function isMissingTableError(error: PostgrestError): boolean {
  return error.code === '42P01'
    || error.code === 'PGRST205'
    || /relation .+ does not exist/i.test(error.message)
    || /could not find the table/i.test(error.message);
}

async function fetchAllRows(candidate: TableCandidate): Promise<CsvRow[]> {
  const rows: CsvRow[] = [];
  let from = 0;

  while (true) {
    let query = supabase.from(candidate.table).select('*');
    if (candidate.hasDeletedAt) query = query.is('deleted_at', null);

    const { data, error } = await query.order('id', { ascending: true }).range(from, from + PAGE_SIZE - 1);
    if (error) throw error;

    const page = (data || []) as CsvRow[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
    from += PAGE_SIZE;
  }
}

async function exportTable(definition: ExportDefinition): Promise<
  | { status: 'exported'; sourceTable: string; csv: string }
  | { status: 'skipped' }
  | { status: 'failed'; sourceTable: string; message: string }
> {
  for (const candidate of definition.candidates) {
    try {
      const rows = await fetchAllRows(candidate);
      return {
        status: 'exported',
        sourceTable: candidate.table,
        csv: rowsToCsv(rows, candidate.columns),
      };
    } catch (error) {
      const postgrestError = error as PostgrestError;
      if (isMissingTableError(postgrestError)) continue;
      return {
        status: 'failed',
        sourceTable: candidate.table,
        message: postgrestError.message || 'Unknown export error.',
      };
    }
  }

  return { status: 'skipped' };
}

function backupFileName(now: Date): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `courtcaptain-backup-${year}-${month}-${day}.zip`;
}

export async function createAndDownloadBackup(options: BackupOptions): Promise<BackupResult> {
  if (!options.isAdmin) throw new Error('Administrative access is required to create a backup.');

  const now = options.now || new Date();
  const files: ZipFile[] = [];
  const tables: string[] = [];
  const skippedTables: string[] = [];
  const failures: BackupFailure[] = [];
  const sourceTables: Record<string, string> = {};

  for (const definition of exportDefinitions) {
    const result = await exportTable(definition);
    if (result.status === 'skipped') {
      skippedTables.push(definition.exportName);
      continue;
    }
    if (result.status === 'failed') {
      failures.push({
        table: definition.exportName,
        sourceTable: result.sourceTable,
        message: result.message,
      });
      continue;
    }

    tables.push(definition.exportName);
    sourceTables[definition.exportName] = result.sourceTable;
    files.push({ name: `${definition.exportName}.csv`, contents: result.csv });
  }

  const manifest: BackupManifest = {
    application: 'CourtCaptain',
    version: 1,
    exportedAt: now.toISOString(),
    exportedBy: options.exportedBy,
    tableCount: tables.length,
    tables,
    sourceTables,
    skippedTables,
    failures,
  };
  files.unshift({ name: 'manifest.json', contents: `${JSON.stringify(manifest, null, 2)}\n` });

  const fileName = backupFileName(now);
  const blob = await createZipBlob(files);
  downloadBlob(blob, fileName);
  return { fileName, manifest };
}
